export const appleClientId = process.env.REACT_APP_CLUBHOUSE_APPLE_CLIENT_ID?.trim() ?? '';
export const appleRedirectUri = process.env.REACT_APP_CLUBHOUSE_APPLE_REDIRECT_URI?.trim() ?? '';
export const appleConfigured = appleClientId.length > 0 && appleRedirectUri.length > 0;

interface AppleAuthorizationResponse {
  authorization: {
    id_token: string;
    state?: string;
  };
  user?: {
    name?: { firstName?: string; lastName?: string };
    email?: string;
  };
}

interface AppleSdk {
  auth: {
    init(options: {
      clientId: string;
      scope: string;
      redirectURI: string;
      state: string;
      nonce: string;
      usePopup: boolean;
    }): void;
    signIn(): Promise<AppleAuthorizationResponse>;
  };
}

declare global {
  interface Window {
    AppleID?: AppleSdk;
  }
}

export interface AppleCredential {
  identityToken: string;
  nonce: string;
  displayName?: string;
}

let sdkPromise: Promise<AppleSdk> | undefined;

function randomValue(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function loadAppleSdk(): Promise<AppleSdk> {
  if (!appleConfigured) return Promise.reject(new Error('Apple sign-in is not configured.'));
  if (window.AppleID) return Promise.resolve(window.AppleID);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('apple-signin-sdk') as HTMLScriptElement | null;
    const resolveSdk = () => window.AppleID
      ? resolve(window.AppleID)
      : reject(new Error('Apple Sign In SDK did not initialize.'));
    if (existing) {
      existing.addEventListener('load', resolveSdk, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'apple-signin-sdk';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = resolveSdk;
    script.onerror = () => reject(new Error('Apple Sign In SDK could not be loaded.'));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

export async function getAppleCredential(): Promise<AppleCredential> {
  const sdk = await loadAppleSdk();
  const nonce = randomValue();
  const state = randomValue();
  sdk.auth.init({
    clientId: appleClientId,
    scope: 'name email',
    redirectURI: appleRedirectUri,
    state,
    nonce: await sha256(nonce),
    usePopup: true,
  });
  const response = await sdk.auth.signIn();
  if (!response.authorization.id_token || response.authorization.state !== state) {
    throw new Error('Apple returned an invalid authorization response.');
  }

  const displayName = [response.user?.name?.firstName, response.user?.name?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || undefined;
  return { identityToken: response.authorization.id_token, nonce, displayName };
}